<template>
  <div class="form-group">
    <label v-text="formatLabel()" />
    <div
      id="file-dropzone"
      :class="{active: inDrag}"
      class="clearfix"
      @dragenter="inDrag = true"
      @dragleave="inDrag=false"
      @drop="inDrag=false"
    >
      <input
        type="file"
        multiple="multiple"
        title="Drag and drop, or click, to add files"
        @change="addFiles"
      >
      <div
        v-for="file in currentFiles"
        :key="`file${file.toString()}`"
        class="file"
      >
        <div
          :style="(file['base64']) ? `background-image: url(${file['base64']})` : null"
          class="file-content"
        >
          <font-awesome-icon
            v-if="!file['base64']"
            style="color: #fff"
            spin
            icon="circle-notch"
          />
          <span
            class="filename"
            v-text="file.name"
          />
          <button
            class="btn btn-apex-primary"
            @click.prevent="removeFile(file)"
          >
            Remove file
          </button>
        </div>
      </div>
      <i v-if="!currentFiles.length">Drag and drop, or click, to add files</i>
    </div>
    <div class="dropzone-footer">
      <span v-text="totalMB" />MB of <span v-text="maxTotal/1000000" />MB
      <div class="pull-right">
        Maximum individual file size: <span v-text="maxIndividual/1000000" />MB
      </div>
    </div>
    <input
      v-model="currentFiles"
      v-validate="validation"
      :name="`form-field-${name}`"
      :data-vv-as="formatLabel()"
      type="hidden"
    >
    <span
      v-if="errors.has(`form-field-${name}`)"
      class="has-error"
      v-text="errors.first(`form-field-${name}`)"
    />
    <span
      v-if="info"
      class="help-block"
      v-text="info"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import pdfjs from 'pdfjs-dist';
import field from '../field.vue';

const pdfjsSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// noinspection JSUnresolvedVariable
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsSrc;
export default {
  extends: field,
  data() {
    return {
      currentFiles: [],
      inDrag: false,
      allowedTypes: ['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'],
      maxTotal: 300000000,
      maxIndividual: 20000000,
    };
  },
  computed: {
    totalMB() {
      return Math.round((_.sumBy(this.currentFiles, 'size') / 1000000) * 100) / 100;
    },
  },
  methods: {
    addFiles(e) {
      const app = this;
      const { files } = e.target;

      if ((_.sumBy(files, 'size') + _.sumBy(app.currentFiles, 'size')) > app.maxTotal) return false;

      _.forEach(files, (file) => {
        if (
          file.size <= app.maxIndividual
            && _.find(app.allowedTypes, (type) => type === file.type)
            && !_.find(app.currentFiles, (currentFile) => file.name === currentFile.name)
        ) {
          const ix = (app.currentFiles.push(file)) - 1;
          const reader = new FileReader();
          reader.addEventListener('load', () => {
            if (file.type.indexOf('pdf') > -1) {
              pdfjs.getDocument(reader.result).then((pdf) => {
                pdf.getPage(1).then((page) => {
                  const viewport = page.getViewport(1);
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  canvas.height = viewport.height;
                  canvas.width = viewport.width;
                  page.render({ canvasContext: ctx, viewport })
                    .promise.then(() => {
                      app.$set(file, 'base64', canvas.toDataURL('image/jpeg'));
                      app.currentFiles.splice(ix, 1, file);
                    });
                });
              });
            } else {
              app.$set(file, 'base64', reader.result);
              app.currentFiles.splice(ix, 1, file);
            }
          });
          if (file.type.indexOf('image') > -1) {
            reader.readAsDataURL(file);
          } else {
            reader.readAsArrayBuffer(file);
          }
        }
      });
      e.target.value = '';
      app.$emit('input', app.currentFiles);
      return null;
    },
    removeFile(file) {
      const app = this;
      app.currentFiles = _.filter(app.currentFiles, (currentFile) => !_.isEqual(file, currentFile));
      app.$emit('input', app.currentFiles);
    },
  },
};
</script>

<style type="text/scss" lang="scss" scoped="scoped">
@import './resources/assets/sass/variables';

#file-dropzone {
  background-color: $grey;
  min-height: 150px;
  position: relative;
  border: 2px transparent solid;

  input[type=file] {
    position: absolute;
    cursor: pointer;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    z-index: 0;
  }
  i {
    position: absolute;
    font-size: 1.1em;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
  .file {
    width: 25%;
    height: 0;
    padding-bottom: 25%;
    position: relative;
    float: left;
    pointer-events: none;
    .file-content {
      background-size: cover;
      background: $primary no-repeat center center;
      position: absolute;
      top: 15px;
      right: 15px;
      bottom: 15px;
      left: 15px;
      padding: 5px;
      span.filename {
        background-color: rgba(255,255,255,0.7);
        padding: 5px;
        display: block;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
      }
      button {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        opacity: 0;
        pointer-events: all;
        &:hover {
          opacity: 1;
        }
      }
    }
  }
  &.active {
    border: 2px $primary dashed;
    .file {
      opacity: 0.5;
      z-index: -1;
    }
  }
}
.dropzone-footer {
  width: 100%;
  background-color: $primary;
  color: $white;
  padding: 5px;
}
</style>
